import { useCallback } from "react";
import { ChatMessage } from "../types";

export const useChatActions = () => {
  const copyConversationToClipboard = useCallback(async (messages: ChatMessage[]): Promise<boolean> => {
    if (messages.length === 0) {
      return false;
    }

    const conversationText = messages
      .map((msg) => {
        const sender = msg.sender === "user" ? "You" : "Dumont";
        const timestamp = msg.timestamp.toLocaleString();
        return `[${timestamp}] ${sender}: ${msg.text}`;
      })
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(conversationText);
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  const exportChatAsJSON = useCallback((messages: ChatMessage[], conversationTitle?: string): boolean => {
    if (messages.length === 0) {
      return false;
    }

    try {
      const exportData = {
        title: conversationTitle || "Dumont Conversation",
        exportedAt: new Date().toISOString(),
        messages: messages.map((msg) => ({
          sender: msg.sender,
          text: msg.text,
          timestamp: msg.timestamp.toISOString(),
        })),
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.download = `dumont-chat-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  const exportChatAsText = useCallback((messages: ChatMessage[], conversationTitle?: string): boolean => {
    if (messages.length === 0) {
      return false;
    }

    try {
      const header = `${conversationTitle || "Dumont Conversation"}\nExported: ${new Date().toLocaleString()}\n${"=".repeat(50)}\n\n`;
      
      const conversationText = messages
        .map((msg) => {
          const sender = msg.sender === "user" ? "You" : "Dumont";
          const timestamp = msg.timestamp.toLocaleString();
          return `[${timestamp}] ${sender}:\n${msg.text}`;
        })
        .join("\n\n" + "-".repeat(50) + "\n\n");

      const fullText = header + conversationText;

      const blob = new Blob([fullText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.download = `dumont-chat-${timestamp}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  return {
    copyConversationToClipboard,
    exportChatAsJSON,
    exportChatAsText,
  } as const;
};
